import {getMetricIdentifiersFromExpressions} from '../../../util/plotHelpers/plotHelpers';
import {LineConfig} from '../config/types';
import {BucketedLine} from '../types';
import {
  evaluateExpression,
  Expression,
  metricsInExpression,
} from './../../../util/expr';
import {Line} from './../../../util/plotHelpers';
import {filterRedundantHistories} from './filterRedundantHistories';
import {SupplementalMetricsByRun} from './types';

export function convertLineDataToTimestamp(lines: Line[]) {
  lines.forEach(line => {
    line.data.forEach(p => {
      p.x = p.x * 1000;
    });
  });
}

/**
 * Evaluate the x expression for each point in the line
 */
export function evaluateXExpressions(
  expression: Expression,
  lines: Line[],
  supplementalMetricsByRun: SupplementalMetricsByRun
) {
  try {
    lines.forEach(line => {
      line.data.forEach(p => {
        const xyData = {
          x: p.x,
          y: p.y,
          ...(p.y0 !== undefined ? {y0: p.y0} : {}),
        };
        p.x = evaluateExpression(expression, {
          ...xyData,
          ...supplementalMetricsByRun[line.name!],
          ...(p.expressionValues as Record<string, any>),
        });
      });
    });
  } catch (e) {
    console.error('Error evaluating x expression', e);
  }
}

/**
 * Any line with a single point where y !== y0 has a valid area to be displayed
 */
export function removeUselessAreaLines(lines: Line[]) {
  return lines.filter(l => {
    if (l.meta.type !== 'area') {
      return true;
    }

    return l.data.some(p => p.y !== p.y0);
  });
}

export function determineXMetrics(
  lineConfig: Pick<LineConfig, 'xAxis' | 'xExpression'>
) {
  const xExpressionMetricIdentifiers = lineConfig.xExpression
    ? metricsInExpression(lineConfig.xExpression, false)
    : [];

  return [
    ...new Set(
      [
        ...xExpressionMetricIdentifiers.concat(lineConfig.xAxis, '_step'),
      ].filter(metric => !!metric)
    ),
  ];
}

export function determineYMetrics(
  lineConfig: Pick<LineConfig, 'yAxis' | 'expressions' | 'xExpression'>
) {
  const {expressionMetricIdentifiers} = getMetricIdentifiersFromExpressions(
    lineConfig.expressions,
    lineConfig.xExpression
  );

  return [
    ...new Set(
      [...lineConfig.yAxis.concat(expressionMetricIdentifiers)].filter(
        metric => !!metric
      )
    ),
  ];
}

export function checkIfUsingExpressions(lineConfig: LineConfig) {
  return (
    !!lineConfig.xExpression ||
    (lineConfig.expressions && lineConfig.expressions.length > 0)
  );
}

export function checkIfUsingTimeConversion(lineConfig: LineConfig) {
  return (
    lineConfig.xAxis === '_runtime' ||
    (lineConfig.xAxis === '_absolute_runtime' && lineConfig.xExpression == null)
  );
}

/**
 * Mutates runs to make them more efficient process
 */
export function preProcessData(data: BucketedLine[], yMetrics: string[]) {
  /**
   * When plotting metrics by custom x-axis we get a bunch of redundant data results. Since the x-axis values come in on the history with each metric we can just strip off the redundant returns and save some processing time.
   *
   * TODO: eliminate these redundant requests in the sampledHistorySpecs
   */
  data.forEach(run => {
    run.history = filterRedundantHistories(run.history, yMetrics);
  });
}
